// =====================================================================
// Launcher.jsx - Page d'accueil type "app launcher" Odoo : grille de
// tuiles colorées plein écran (sans barre latérale), compte en haut à
// droite. Chaque tuile est filtrée par les mêmes permissions que la nav.
// =====================================================================
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { usePendingCounts } from '../hooks/usePendingCounts.jsx';
import { AppIcon } from '../components/AppIcons.jsx';
import AccountMenu from '../components/AccountMenu.jsx';
import { DROITS_GERER_EQUIPES } from '../lib/droits-equipes.js';
import { droitsCommandesPretes } from './commande-prete/droits.js';

export default function Launcher() {
  const { can } = useAuth();
  const pending = usePendingCounts();

  const tiles = [];

  // Accueil (toujours présent, selon le rôle)
  if (can('users.create.all')) tiles.push({ name: 'Tableau de bord', icon: 'dash', to: '/dashboard' });
  else tiles.push({ name: 'Mon espace', icon: 'dash', to: '/mon-espace' });

  // --- Quotidien ---
  if (can('timesheets.read.own') || can('timesheets.read.all') || can('timesheets.approve.group') || can('presence.read.all') || can(['overtime.read.all', 'overtime.read.own', 'overtime.create.all']))
    tiles.push({ name: 'Temps & Présence', icon: 'clock', to: '/temps', badge: pending.timesheets });
  if (can('leaves.create.own') || can('leaves.approve.all'))
    tiles.push({ name: 'Congés', icon: 'leave', to: '/conges', badge: pending.conges });
  if (can('trips.read.all') || can('trips.complete.assigned'))
    tiles.push({ name: 'Trajets', icon: 'truck', to: '/trajets' });
  if (can(['supply_draws.read.own', 'supply_draws.read.all']))
    tiles.push({ name: 'Prélev. fourn.', icon: 'basket', to: '/prelevements' });
  if (can(['printing_jobs.read.own', 'printing_jobs.read.all']))
    tiles.push({ name: 'Printing rapport', icon: 'print', to: '/impressions' });
  if (can('inventory.read.all'))
    tiles.push({ name: 'Inventaire mensuel', icon: 'cube', to: '/inventaire' });
  if (can('pallet_tracking.read.all'))
    tiles.push({ name: 'Palettes in/out', icon: 'pallet', to: '/palettes' });
  if (droitsCommandesPretes(can).lire) {
    // Commandes prêtes (13-10-2026, v2 multi-client 16-10-2026) : palettes/colis mesurés +
    // photos, mail « commande prête » au client, chargement, paramètres client. Droits PROPRES
    // au module depuis le 17/10 (SPEC v3 D) : la tuile = « Voir les commandes prêtes ». La tuile
    // « Dimensions colis » est retirée (décision Silviu 14/10) : la route /dimensions reste
    // accessible par URL et l'export EffiTrace M-009 continue.
    // v3 B (16-10-2026) : pastille = rendez-vous d'enlèvement pas encore vus + documents à imprimer.
    tiles.push({ name: 'Commandes prêtes', icon: 'pallet', to: '/commande-prete', badge: pending.commandesPretes });
  }
  // M-015 (29-08-2026) — Remise transporteur : bordereaux EffiTrace signes au quai.
  if (can(['bordereaux.sign.all', 'bordereaux.read.all', 'bordereaux.auto.all']))
    tiles.push({ name: 'Remise transporteur', icon: 'slip', to: '/bordereaux' });
  // M-010 (25-08-2026) — Sécurité & routines. Visible dès qu'on peut déclarer
  // un accident, c.-à-d. pour tout le monde : le registre lui-même reste
  // protégé par safety.read.all à l'intérieur de la page.
  if (can('safety.declare.own')) {
    // Deux tuiles depuis le 26/08 (demande de Florent) : le registre des
    // premiers soins contient des donnees de sante, il ne partage plus
    // son entree avec le nettoyage des tables de packing. L'accident
    // passe en premier : c'est celui qu'on cherche en urgence.
    tiles.push({ name: 'Accidents & premiers soins', icon: 'firstaid',
      to: '/accidents' });
    tiles.push({ name: 'Sécurité & routines', icon: 'shield', to: '/securite',
      badge: pending.safety });
  }
  // M-006 — Erreurs (badge = mes erreurs non-vues)
  if (can('errors.read.own'))
    tiles.push({ name: 'Erreurs', icon: 'alert', to: '/erreurs', badge: pending.errors });
  // M-007 — Facturation (admin/finance only)
  if (can('facturation.read.all'))
    tiles.push({ name: 'Facturation', icon: 'invoice', to: '/facturation' });
  // M-008 — Convoyeur (tri colis : recherche, vue d'ensemble, journal, réconciliation)
  if (can(['convoyeur.read.all', 'convoyeur.manage.all']))
    tiles.push({ name: 'Convoyeur', icon: 'conveyor', to: '/convoyeur' });

  // --- Administration ---
  // Qui GÈRE les équipes ouvre aussi la tuile (page limitée à l'onglet Groupes).
  if (can(['users.create.all', 'users.update.all']) || can(DROITS_GERER_EQUIPES))
    tiles.push({ name: 'Utilisateurs', icon: 'users', to: '/admin/users' });
  if (can(['projects.create.all', 'projects.update.all']))
    tiles.push({ name: 'Projets', icon: 'check', to: '/admin/projects' });
  if (can('timesheets.lock_period.all'))
    tiles.push({ name: 'Verrouillage', icon: 'lock', to: '/admin/period-locks' });
  if (can('permissions.read.all'))
    tiles.push({ name: 'Permissions', icon: 'key', to: '/admin/permissions' });
  if (can('medical_certificates.read'))
    tiles.push({ name: 'Justificatifs médicaux', icon: 'med', to: '/certificats-medicaux' });

  // Day 11 — Cahier de charge : lecture pour tout user authentifié
  tiles.push({ name: 'Cahiers de charge', icon: 'cahier', to: '/cahiers-charge' });

  return (
    <div style={styles.page}>
      <div style={styles.topbar}>
        <span style={styles.brand}>Brain E-Log</span>
        <AccountMenu />
      </div>

      <div style={styles.grid}>
        {tiles.map((t) => (
          <Link key={t.to} to={t.to} style={styles.app} className="launcher-app">
            <span style={styles.tile} className="launcher-tile">
              <AppIcon name={t.icon} size={44} />
              {t.badge > 0 && <span style={styles.badge}>{t.badge}</span>}
            </span>
            <span style={styles.name}>{t.name}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #e7e4f0 0%, #f1f1f6 55%, #eceef3 100%)',
  },
  topbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 24px', position: 'sticky', top: 0,
  },
  brand: { color: '#714B67', fontWeight: 800, fontSize: '1.05rem' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(108px, 1fr))',
    gap: '28px 14px',
    maxWidth: 760,
    margin: '0 auto',
    padding: '4vh 24px 6vh',
  },
  app: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    textDecoration: 'none', color: '#3f4654',
  },
  tile: {
    position: 'relative',
    width: 76, height: 76, background: '#fff', borderRadius: 16,
    boxShadow: '0 4px 14px rgba(60,50,90,0.10)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'transform 0.12s, box-shadow 0.12s',
  },
  badge: {
    position: 'absolute', top: -6, right: -6, minWidth: 20, height: 20, padding: '0 6px',
    background: '#ef4444', color: '#fff', fontSize: '0.7rem', fontWeight: 700,
    borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '2px solid #fff',
  },
  name: { marginTop: 9, fontSize: '0.82rem', textAlign: 'center', lineHeight: 1.25 },
};
